/**
 * Оновлення в реальному часі (SSE `/events`, S8 + S9): «повідомити, потім перечитати». Подія
 * несе лише тип, сутність і id — дані клієнт перечитує через API зі своїми правами (SEC-043).
 * Відлуння власної зміни (changeId з відповіді запису) не показуємо як зміну колеги.
 */
import type { RealtimeEvent } from '@clientline/contracts';
import { type Query, type QueryClient, useQueryClient } from '@tanstack/react-query';
import { useEffect } from 'react';
import { ownChanges, platformKeys } from './api.ts';
import { reportNetworkFailure } from './network.ts';
import { hasSessionHint } from './session.ts';

type Listener = (event: RealtimeEvent, own: boolean) => void;
const listeners = new Set<Listener>();

/** Підписка екрана на події (картка показує «Змінено колегою»). */
export function onRecordEvent(listener: Listener): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

/**
 * Події сповіщень `notification.*` (notifications.md, розд. 6): без запису, лише адресату, з
 * плоскими `data`. Їх слухає центр сповіщень (`features/notifications/live.ts`): лічильник, тост.
 */
type NotificationListener = (event: RealtimeEvent) => void;
const notificationListeners = new Set<NotificationListener>();

export function onNotificationEvent(listener: NotificationListener): () => void {
  notificationListeners.add(listener);
  return () => notificationListeners.delete(listener);
}

/**
 * Списки сутності, які на екрані оновлює живе оновлення (LST-18, `list-live.ts`): подія лише
 * позначає їх застарілими, а не перечитує всі порції — рядки не переїжджають під людиною. Решта
 * списків сутності (кеш інших подань) перечитується при наступному показі, як і раніше.
 */
const liveLists = new Map<string, number>();

export function holdListRefresh(entity: string): () => void {
  liveLists.set(entity, (liveLists.get(entity) ?? 0) + 1);
  let held = true;
  return () => {
    if (!held) return;
    held = false;
    const n = (liveLists.get(entity) ?? 1) - 1;
    if (n > 0) liveLists.set(entity, n);
    else liveLists.delete(entity);
  };
}

/**
 * Чи живий потік подій (LST-18): `false` — немає з'єднання чи обрив, список переходить на
 * опитування раз на 60 с. Підписка — для живого оновлення списку.
 */
let streamOpen = false;
const streamListeners = new Set<() => void>();
export const isStreamOpen = () => streamOpen;
export function onStreamState(listener: () => void): () => void {
  streamListeners.add(listener);
  return () => streamListeners.delete(listener);
}
function setStreamOpen(open: boolean) {
  if (open === streamOpen) return;
  streamOpen = open;
  for (const listener of streamListeners) listener();
}

/**
 * Відлуння власної зміни (аудит № 22): автор зміни сам оновив те, що на екрані (`setQueryData` з
 * відповіді, власна інвалідація), а подія приходить за ним за десятки мілісекунд. Друге
 * перечитування не отримує запит, що отримав дані щойно (за це вікно), і запит у дорозі, який
 * почався вже після відповіді зміни (його запустив сам автор, він читає зроблене). Запит у дорозі,
 * що почався раніше (чи невідомо коли), перезапускається: він міг читати до коміту зміни (дві
 * швидкі правки поспіль). Решта ключів запису — як для чужої зміни: автор міг оновити не все
 * (лінія запису, пов'язане). Списки сутності подія позначає застарілими завжди: їх перечитує показ
 * або живе оновлення, а не ця подія.
 */
export const OWN_ECHO_FRESH_MS = 2_000;

/**
 * Коли почалося поточне читання запиту (`performance.now()`, як мітки `ownChanges`); стежить
 * `trackFetchStarts`. Перезапуск читання через `cancelRefetch` дію `fetch` не завжди надсилає: тоді
 * лишається мітка попереднього, ранішого початку — і наступне відлуння перезапустить його знову.
 * Це обережний бік: зайве читання, а не пропущена зміна.
 */
const fetchStarts = new WeakMap<Query, number>();

/**
 * Стежити, коли починаються читання кешу `client` (App.tsx): відлуння відрізняє читання, яке автор
 * запустив після своєї відповіді, від того, що почалося раніше. Повертає відписку.
 */
export function trackFetchStarts(client: QueryClient): () => void {
  return client.getQueryCache().subscribe((event) => {
    if (event.type === 'updated' && event.action.type === 'fetch')
      fetchStarts.set(event.query, performance.now());
  });
}

const ownEchoStale = (now: number, ownAt: number) => (query: Query) => {
  if (query.state.fetchStatus === 'fetching') {
    const started = fetchStarts.get(query);
    return started === undefined || started < ownAt;
  }
  return now - query.state.dataUpdatedAt > OWN_ECHO_FRESH_MS;
};

/** Подія потоку → що перечитати (експорт — для тестів). */
export function applyEvent(client: QueryClient, event: RealtimeEvent) {
  if (event.type === 'schema.changed') {
    void client.invalidateQueries({ queryKey: platformKeys.meta });
    // Поля чи вид довідника могли змінитись (references.md, розд. 4, п. 3).
    void client.invalidateQueries({ queryKey: platformKeys.references });
    return;
  }
  if (event.type.startsWith('notification.')) {
    for (const listener of notificationListeners) listener(event);
    return;
  }
  // Будь-яка подія з сутністю довідника скидає його кеш, зокрема `reference.changed` — сигнал без
  // id про запис, якого людина вже не бачить (кошик, вихід з області; references.md, розд. 4).
  // Кеш є лише у відкритого довідника: для інших сутностей це порожня операція.
  if (event.entity)
    void client.invalidateQueries({ queryKey: platformKeys.reference(event.entity) });
  if (!event.entity || !event.recordId) return;
  const own = ownChanges.has(event.id);
  const predicate = own
    ? {
        predicate: ownEchoStale(Date.now(), ownChanges.at(event.id) ?? Number.POSITIVE_INFINITY),
      }
    : {};
  void client.invalidateQueries({
    queryKey: platformKeys.lists(event.entity),
    ...(liveLists.has(event.entity) ? { refetchType: 'none' as const } : {}),
  });
  const recordKeys = [
    platformKeys.record(event.entity, event.recordId),
    platformKeys.history(event.entity, event.recordId),
    // Панелі пов'язаного запису (REC-12): записи дочірніх сутностей змінюються разом із ним.
    platformKeys.related(event.entity, event.recordId),
    // Лінія запису: `timeline.changed` (предмети й події піддерева) і власні зміни запису (аудит).
    platformKeys.timeline(event.entity, event.recordId),
  ];
  for (const queryKey of recordKeys) void client.invalidateQueries({ queryKey, ...predicate });
  for (const listener of listeners) listener(event, own);
}

/**
 * Одне з'єднання на вкладку, поки людина ввійшла. Обрив — EventSource перепідключається сам.
 *
 * Кеш «назад / вперед» (bfcache, UX-34): відкрите з'єднання може не пустити сторінку в кеш, а
 * події, що прийшли б, поки вона там, губляться. Тому на `pagehide` з `persisted` з'єднання
 * закриваємо, а коли сторінка повернулась із кешу (`pageshow` з `persisted`), відкриваємо знову й
 * перечитуємо все видиме, як після `reset`: пропущене за час у кеші інакше не підтягнеться.
 * Якщо за час у кеші людина вийшла (ознаки входу немає), сторінку веде на вхід `watchSignOut`
 * (sign-out.ts): тоді ні з'єднання, ні запитів — вони дали б лише 401.
 */
export function usePlatformEvents(enabled: boolean) {
  const client = useQueryClient();
  useEffect(() => {
    if (!enabled || typeof EventSource === 'undefined') return;
    let source: EventSource | null = null;
    const connect = () => {
      const next = new EventSource('/events');
      source = next;
      // Перепідключення після обриву: події між з'єднаннями могли загубитись — довідники
      // перечитати (`If-None-Match`, без зміни — `304`). Перше відкриття кеш не скидає.
      let opened = false;
      next.onopen = () => {
        if (opened) void client.invalidateQueries({ queryKey: platformKeys.references });
        opened = true;
        setStreamOpen(true);
      };
      next.onmessage = (message) => {
        try {
          applyEvent(client, JSON.parse(message.data) as RealtimeEvent);
        } catch {
          // Пошкоджене повідомлення — пропускаємо, наступне перечитування все одно підтягне стан.
        }
      };
      // Пропущено забагато або фільтр не відпрацював (fail closed) — перечитати все видиме.
      next.addEventListener('reset', () => void client.invalidateQueries({ queryKey: ['p'] }));
      // Обрив потоку — ще один факт для стану мережі (UX-29): чи є зв'язок, скаже перевірка.
      next.onerror = () => {
        setStreamOpen(false);
        reportNetworkFailure();
      };
    };
    const onPageHide = (event: PageTransitionEvent) => {
      if (!event.persisted) return;
      source?.close();
      source = null;
      setStreamOpen(false);
    };
    const onPageShow = (event: PageTransitionEvent) => {
      if (!event.persisted || source || !hasSessionHint()) return;
      connect();
      void client.invalidateQueries({ queryKey: ['p'] });
    };
    connect();
    window.addEventListener('pagehide', onPageHide);
    window.addEventListener('pageshow', onPageShow);
    return () => {
      window.removeEventListener('pagehide', onPageHide);
      window.removeEventListener('pageshow', onPageShow);
      source?.close();
      setStreamOpen(false);
    };
  }, [enabled, client]);
}
